import { Box, Grid, Typography } from "@mui/material";
import { languageOptionsAtom, shouldCloseMenuAtom } from "atoms";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";

interface LanguageOption {
  value: string;
  name: string;
}

interface LanguageMenuItemsProps {
  onLanguageClick: (value: string) => void;
  currentLanguage?: string | null;
  languageOptions?: LanguageOption[];
}

export function LanguageMenuItems({
  onLanguageClick,
  currentLanguage,
  languageOptions: providedLanguageOptions,
}: LanguageMenuItemsProps): JSX.Element {
  const setShouldCloseMenu = useSetAtom(shouldCloseMenuAtom);
  const defaultLanguageOptions = useAtomValue(languageOptionsAtom);
  const { t } = useTranslation();
  const languageOptions = providedLanguageOptions || defaultLanguageOptions;

  useEffect(() => {
    setShouldCloseMenu(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container spacing={1} sx={{ p: 1 }}>
      {languageOptions.map((language) => {
        const isSelected = currentLanguage === language.value;
        return (
          <Grid item xs={4} key={language.value}>
            <Box
              data-label={language.name}
              data-value={language.value}
              onClick={() => onLanguageClick(language.value)}
              sx={{
                width: "100%",
                cursor: "pointer",
                backgroundColor: isSelected ? "#ebf7f7" : "white",
                border: isSelected
                  ? "1px solid rgba(0, 134, 103, 0.5)"
                  : "1px solid transparent",
                transition: "background-color 0.3s, border-color 0.3s",
                padding: 2,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                position: "relative",
                "&:hover": {
                  backgroundColor: isSelected
                    ? "#ebf7f7"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.primary">
                  {language.value === "automatic"
                    ? language.name
                    : t(
                        `tts.toolbarMenu.languageMenu.${language.value.toLowerCase()}`
                      )}
                </Typography>
              </Box>
              {isSelected && (
                <CheckIcon
                  color="primary"
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}
